import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import PromptAIHelpButton from './PromptAIHelpButton';

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}

interface SpeechRecognitionErrorEventLike {
  error: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

interface SpeechRecognitionWindow extends Window {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
}

interface SelectedChatImage {
  id: string;
  previewUrl: string;
  uploading?: boolean;
}

interface ChatInputBarProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  disabled?: boolean;
  submitDisabled?: boolean;
  capacityMessage?: string | null;
  onAIHelp?: () => void;
  aiHelpProfile?: string | null;
  aiHelpDisabled?: boolean;
  aiHelpLoading?: boolean;
  aiHelpError?: string | null;
  pointsCost: number | null;
  onImagePick?: () => void;
  selectedImages?: SelectedChatImage[];
  onRemoveImage?: (id: string) => void;
  imageUploadError?: string | null;
  selectedModel?: string;
  selectedModelName?: string;
  onOpenModel?: () => void;
}

export default function ChatInputBar({
  value,
  onChange,
  onSend,
  disabled,
  submitDisabled = false,
  capacityMessage,
  onAIHelp,
  aiHelpProfile,
  aiHelpDisabled = false,
  aiHelpLoading = false,
  aiHelpError,
  onImagePick,
  selectedImages = [],
  onRemoveImage,
  imageUploadError,
  pointsCost,
  selectedModel,
  selectedModelName,
  onOpenModel,
}: ChatInputBarProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const valueRef = useRef(value);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
    }
  }, [value]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if ((value.trim() || selectedImages.length > 0) && !disabled && !submitDisabled) onSend();
    }
  };

  const appendTranscript = useCallback((transcript: string) => {
    const text = transcript.trim();
    if (!text) return;
    const currentValue = valueRef.current;
    const separator = currentValue.trim().length > 0 && !/[\s，。,.!?！？]$/.test(currentValue) ? '，' : '';
    onChange(`${currentValue}${separator}${text}`);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [onChange]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const startListening = useCallback(() => {
    if (disabled) return;
    setVoiceError(null);

    if (isListening) {
      if (window.__NATIVE_BRIDGE__?.stopSpeechRecognition) {
        void window.__NATIVE_BRIDGE__.stopSpeechRecognition().catch(() => {});
      }
      stopListening();
      return;
    }

    if (window.__NATIVE_BRIDGE__?.startSpeechRecognition) {
      setIsListening(true);
      setVoiceError(null);
      window.__NATIVE_BRIDGE__.startSpeechRecognition()
        .then(({ transcript }) => {
          appendTranscript(transcript);
          setVoiceError(null);
        })
        .catch((err) => {
          const message = String(err?.message ?? err ?? '');
          if (/permission|denied|not authorized|record/i.test(message)) {
            setVoiceError(t('chat_voicePermissionDenied', '请允许麦克风权限后再试'));
          } else if (/no speech|empty/i.test(message)) {
            setVoiceError(t('chat_voiceNoSpeech', '没有识别到语音，请重试'));
          } else {
            setVoiceError(t('chat_voiceFailed', '语音识别失败，请重试'));
          }
        })
        .finally(() => {
          setIsListening(false);
        });
      return;
    }

    const speechWindow = window as SpeechRecognitionWindow;
    const SpeechRecognition = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceError(t('chat_voiceUnsupported', '当前浏览器不支持语音输入'));
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = t('chat_voiceLang', 'zh-CN');

    let finalText = '';

    recognition.onstart = () => {
      setIsListening(true);
      setVoiceError(null);
    };
    recognition.onresult = (event) => {
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }
      if (interimText) {
        setVoiceError(t('chat_voiceListeningWithText', { text: interimText }));
      }
    };
    recognition.onerror = (event) => {
      const message = event.error === 'not-allowed'
        ? t('chat_voicePermissionDenied', '请允许麦克风权限后再试')
        : t('chat_voiceFailed', '语音识别失败，请重试');
      setVoiceError(message);
    };
    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
      if (finalText.trim()) {
        appendTranscript(finalText);
        setVoiceError(null);
      }
    };

    try {
      recognition.start();
    } catch {
      setIsListening(false);
      recognitionRef.current = null;
      setVoiceError(t('chat_voiceFailed', '语音识别失败，请重试'));
    }
  }, [appendTranscript, disabled, isListening, stopListening, t]);

  useEffect(() => () => {
    recognitionRef.current?.abort();
    recognitionRef.current = null;
  }, []);

  const hasText = value.trim().length > 0;
  const hasImages = selectedImages.length > 0;

  const modelIcons: Record<string, string> = {
    doubao: 'ri-leaf-line',
    gpt: 'ri-robot-2-line',
    gpt_image_2: 'ri-robot-2-line',
    gemini: 'ri-image-ai-line',
    gemini_nano: 'ri-image-ai-line',
    nanobanana: 'ri-magic-line',
  };
  const modelInfo = {
    name: selectedModelName || selectedModel,
    icon: modelIcons[selectedModel] || 'ri-robot-2-line',
  };

  return (
    <div className="relative z-20 pb-0">
      <div className="px-4 pt-1 pb-2">
        <div
          className="rounded-[24px] bg-white transition-all duration-200"
          style={{
            border: isFocused ? '1px solid rgba(255,107,157,0.2)' : '1px solid rgba(0,0,0,0.05)',
            boxShadow: isFocused ? '0 10px 28px rgba(255,107,157,0.12)' : '0 10px 26px rgba(17,24,39,0.06)',
          }}
        >
          <div className="px-4 pt-3 pb-1.5">
            {hasImages && (
              <div className="mb-2 flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                {selectedImages.map((image, index) => (
                  <div key={image.id} className="relative w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100" style={{ border: '1px solid rgba(255,107,157,0.18)' }}>
                    <img src={image.previewUrl} alt={`reference ${index + 1}`} className="w-full h-full object-contain" />
                    {image.uploading && (
                      <div className="absolute inset-0 bg-white/65 flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => onRemoveImage?.(image.id)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-gray-900/65 text-white flex items-center justify-center cursor-pointer active:scale-95"
                    >
                      <i className="ri-close-line text-[11px]" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {imageUploadError && (
              <div className="mb-1.5 flex items-center gap-1 text-[11px] text-red-400">
                <i className="ri-error-warning-line text-xs" />
                <span>{imageUploadError}</span>
              </div>
            )}
            {aiHelpProfile?.trim() && aiHelpError && (
              <div role="alert" data-testid="ai-help-error" className="mb-1.5 flex items-center gap-1 text-[11px] text-red-400">
                <i className="ri-error-warning-line text-xs" />
                <span>{aiHelpError}</span>
              </div>
            )}
            {(voiceError || isListening) && (
              <div className={`mb-1.5 flex items-center gap-1 text-[11px] ${isListening ? 'text-pink-500' : 'text-red-400'}`}>
                <i className={`${isListening ? 'ri-mic-fill animate-pulse' : 'ri-error-warning-line'} text-xs`} />
                <span>{voiceError || t('chat_voiceListening', '正在聆听...')}</span>
              </div>
            )}
            <textarea
              ref={textareaRef}
              data-testid="generation-prompt-input"
              aria-label="generation-prompt-input"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={hasImages ? t('chat_editPlaceholder', '描述你想怎么修改这些图片，例如：把背景换成海边日落，保留人物') : t('gen_placeholderWithExample', '描述你想要的画面，越详细越好哦～\n例如：一位穿着汉服的女子站在樱花树下，花瓣飘落，梦幻光影，超高清画质')}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              className="w-full bg-transparent text-[15px] text-gray-700 placeholder:text-gray-300 resize-none outline-none leading-relaxed disabled:opacity-50"
              style={{ minHeight: '60px', maxHeight: '112px' }}
              rows={2}
              maxLength={500}
            />
          </div>

          <div className="flex items-center justify-between px-3.5 pb-3">
            <button
              onClick={onOpenModel}
              data-testid="generation-model-selector"
              aria-label="generation-model-selector"
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 cursor-pointer active:scale-95 transition-all duration-200"
              style={{
                background: 'rgba(255,107,157,0.08)',
                border: '1px solid rgba(255,107,157,0.12)',
              }}
            >
              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
                <i className={`${modelInfo.icon} text-white text-[10px]`} />
              </div>
              <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">{modelInfo.name}</span>
              <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
            </button>

            <div className="flex items-center gap-1">
              {onAIHelp ? (
                <PromptAIHelpButton
                  profile={aiHelpProfile}
                  onClick={onAIHelp}
                  disabled={disabled || aiHelpDisabled || !hasText}
                  loading={aiHelpLoading}
                />
              ) : null}

              <button
                type="button"
                onClick={startListening}
                disabled={disabled}
                aria-label={isListening ? t('chat_voiceStop', '停止语音输入') : t('chat_voiceStart', '语音输入')}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer disabled:opacity-50 ${isListening ? 'text-white' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 active:bg-gray-100'}`}
                style={isListening ? {
                  background: 'linear-gradient(135deg, #FF6B9D, #A855F7)',
                  boxShadow: '0 2px 8px rgba(168,85,247,0.24)',
                } : undefined}
              >
                <i className={`${isListening ? 'ri-mic-fill' : 'ri-mic-line'} text-base`} />
              </button>

              <button
                onClick={onImagePick}
                disabled={disabled}
                className={`relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 cursor-pointer ${hasImages ? 'text-pink-500 bg-pink-50' : 'text-gray-400 hover:text-gray-600'} disabled:opacity-50`}
              >
                <i className="ri-image-line text-base" />
                {hasImages && (
                  <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-pink-500 text-white text-[9px] leading-4 font-bold">
                    {selectedImages.length}
                  </span>
                )}
              </button>

              {(hasText || hasImages) && (
                <button
                  onClick={onSend}
                  data-testid="generation-submit"
                  aria-label="generation-submit"
                  disabled={disabled || submitDisabled}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-200 active:scale-90 cursor-pointer ml-0.5"
                  style={{
                    background: disabled || submitDisabled
                      ? 'linear-gradient(135deg, #D1D5DB, #9CA3AF)'
                      : 'linear-gradient(135deg, #FF6B9D, #A855F7)',
                    boxShadow: disabled || submitDisabled
                      ? 'none'
                      : '0 2px 8px rgba(168,85,247,0.25)',
                  }}
                >
                  <i className="ri-arrow-up-line text-sm" />
                </button>
              )}
            </div>
          </div>
        </div>

        {capacityMessage ? (
          <div
            role="status"
            data-testid="generation-capacity-message"
            className="mt-1.5 flex items-center justify-center gap-1 text-[11px] text-amber-600"
          >
            <i className="ri-time-line text-[11px]" />
            <span>{capacityMessage}</span>
          </div>
        ) : (
          <div className="flex items-center justify-center mt-1.5 gap-1">
            <i className="ri-flashlight-fill text-amber-400 text-[10px]" />
            <span
              data-testid="generation-points-cost"
              aria-label={`generation-points-cost-${pointsCost ?? 'unknown'}`}
              className="text-[11px] text-gray-400"
            >
              {t('gen_pointsCost', { cost: pointsCost ?? '--' })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
